import type { RequestOptions } from "@@/plugin-request/request";
import type { RequestConfig } from "@umijs/max";
import { message, notification } from "antd";
import { clearSession, getToken, setToken } from "./utils/auth";

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}
// 与后端约定的响应数据格式
interface ResponseStructure {
  code: number | string;
  msg: string;
  data: any;
}

/** errorThrower 写入 error.info 的结构，与 ResponseStructure 不同 */
interface BizErrorInfo {
  errorMessage: string;
  errorCode: number;
  showType: ErrorShowType;
  data?: unknown;
}

const resolveShowType = (code: number): ErrorShowType => {
  if (code === 401) {
    return ErrorShowType.REDIRECT;
  }
  if (code === 403) {
    return ErrorShowType.WARN_MESSAGE;
  }
  return ErrorShowType.ERROR_MESSAGE;
};

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 统一按后端包装结构 code/msg/data 判定业务错误
    errorThrower: (res) => {
      const payload = res as ResponseStructure;
      const bizCode = Number(payload?.code);
      if (Number.isNaN(bizCode) || bizCode !== 0) {
        const error: any = new Error(payload?.msg || "请求失败");
        error.name = "BizError";
        error.info = {
          errorCode: Number.isNaN(bizCode) ? -1 : bizCode,
          errorMessage: payload?.msg || "请求失败",
          showType: resolveShowType(Number.isNaN(bizCode) ? -1 : bizCode),
          data: payload?.data,
        };
        throw error;
      }
    },
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;
      // 我们的 errorThrower 抛出的错误。
      if (error.name === "BizError") {
        const errorInfo = error.info as BizErrorInfo | undefined;
        if (errorInfo) {
          const { errorMessage, errorCode } = errorInfo;
          switch (errorInfo.showType) {
            case ErrorShowType.SILENT:
              // do nothing
              break;
            case ErrorShowType.WARN_MESSAGE:
              message.warning(errorMessage);
              break;
            case ErrorShowType.ERROR_MESSAGE:
              message.error(errorMessage);
              break;
            case ErrorShowType.NOTIFICATION:
              notification.open({
                description: errorMessage,
                message: errorCode,
              });
              break;
            case ErrorShowType.REDIRECT:
              clearSession();
              window.location.href = "/user/login";
              break;
            default:
              message.error(errorMessage);
          }
        }
      } else if (error.response) {
        // Axios 的错误
        // 请求成功发出且服务器也响应了状态码，但状态代码超出了 2xx 的范围
        message.error(`Response status:${error.response.status}`);
        if (error.response.status === 401) {
          clearSession();
          window.location.href = "/user/login";
        }
      } else if (error.request) {
        // 请求已经成功发起，但没有收到响应
        // \`error.request\` 在浏览器中是 XMLHttpRequest 的实例，
        // 而在node.js中是 http.ClientRequest 的实例
        message.error("None response! Please retry.");
      } else {
        // 发送请求时出了点问题
        message.error("Request error, please retry.");
      }
    },
  },

  // 请求拦截器
  requestInterceptors: [
    (config: RequestOptions) => {
      const token = getToken();
      return {
        ...config,
        headers: {
          ...config.headers,
          ...(token ? { Authorization: token } : {}),
        },
      };
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    (response) => {
      const refreshToken = response.headers?.auth_token as string | undefined;
      if (refreshToken) {
        setToken(refreshToken);
      }

      return response;
    },
  ],
};
